import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAppSelector } from '@/features/hooks';
import { useToast } from '@/hooks/use-toast';
import { useCurrency } from '@/hooks/useCurrency';
import api, { endpoints } from '@/services/api';
import type { TransactionTemplate } from '@/lib/types';

interface QuickCreateCardProps {
  template: TransactionTemplate | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function QuickCreateCard({ template, open, onOpenChange, onSuccess }: QuickCreateCardProps) {
  const { toast } = useToast();
  const { fmt } = useCurrency();
  const accounts = useAppSelector((state) => state.accounts.accounts);

  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState('');
  const [accountId, setAccountId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sync template values to form state when dialog opens
  useEffect(() => {
    if (open && template) {
      setDate(new Date().toISOString().slice(0, 10));
      setAmount(template.amount != null ? String(template.amount) : '');
      const tplAccountId = typeof template.account === 'object' ? (template.account as { _id?: string })?._id : template.account || '';
      setAccountId(tplAccountId || '');
    }
  }, [open, template]);

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
  };

  const handleSubmit = async () => {
    if (!template) return;
    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      toast({ title: 'Invalid amount', description: 'Please enter a positive number.', variant: 'destructive' });
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post(endpoints.transactionTemplates.apply(template._id), {
        date,
        amount: parsedAmount,
        ...(accountId && { accountId }),
      });
      toast({ title: 'Transaction created', description: `Created from template "${template.name}".` });
      onSuccess?.();
      onOpenChange(false);
    } catch (err: unknown) {
      toast({ title: 'Failed to create transaction', description: String(err), variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!template) return null;

  const hasTemplateAccount = !!(typeof template.account === 'object' ? template.account?._id : template.account);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="quick-create-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {template.emoji && <span className="text-lg">{template.emoji}</span>}
            Quick Create: {template.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Date */}
          <div className="space-y-2">
            <Label htmlFor="qc-date">Date</Label>
            <Input
              id="qc-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="min-h-[44px]"
              data-testid="quick-create-date"
            />
          </div>

          {/* Amount */}
          <div className="space-y-2">
            <Label htmlFor="qc-amount">
              Amount {template.amount == null && <span className="text-destructive">*</span>}
            </Label>
            <Input
              id="qc-amount"
              type="number"
              min="0.01"
              step="0.01"
              placeholder={template.amount != null ? fmt(template.amount) : 'Enter amount'}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="min-h-[44px]"
              data-testid="quick-create-amount"
            />
          </div>

          {/* Account Selector — only shown if template has no account */}
          {!hasTemplateAccount && (
            <div className="space-y-2">
              <Label>Account <span className="text-destructive">*</span></Label>
              <Select value={accountId} onValueChange={setAccountId}>
                <SelectTrigger className="min-h-[44px]" data-testid="quick-create-account">
                  <SelectValue placeholder="Select account" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((acc) => (
                    <SelectItem key={acc.id} value={acc.id}>
                      {acc.emoji ? `${acc.emoji} ` : ''}{acc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Template summary */}
          <div className="rounded-md bg-muted/50 p-3 text-sm space-y-1">
            <p><span className="text-muted-foreground">Type:</span> <span className="capitalize">{template.type || 'expense'}</span></p>
            {template.description && <p><span className="text-muted-foreground">Description:</span> {template.description}</p>}
            {template.merchant && <p><span className="text-muted-foreground">Merchant:</span> {template.merchant}</p>}
            {template.tags?.length > 0 && (
              <p><span className="text-muted-foreground">Tags:</span> {template.tags.join(', ')}</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !amount || (!hasTemplateAccount && !accountId)}
            className="min-h-[44px]"
            data-testid="quick-create-submit"
          >
            {isSubmitting ? 'Creating...' : 'Create Transaction'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
